import clsx from 'clsx';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, DateShortRelative } from '@gitbook/spine-react';

import { decodeDocumentFromAPI } from '../BasicEditor';

/**
 * Plain text of a message input's content, for the clipboard.
 */
export function getMessageContentText(content: api.AIMessageInput['content']): string {
    return typeof content === 'string' ? content : decodeDocumentFromAPI(content).text;
}

/**
 * Row of controls under a user message, shown while the message is hovered.
 *
 * Requires a `group/message` on the hovered ancestor. Every message gets the copy control and its
 * time; a message the agent has not picked up yet adds its own controls — put back in the
 * composer, send immediately — through `children`.
 */
export function AgentMessageActions(props: {
    /** Read lazily: the text is only built when the copy control is actually pressed. */
    getText: () => string;
    /** When the message was sent or queued. Omitted for messages recorded before it was kept. */
    date?: string;
    /** Controls specific to the message's state, rendered before the copy control. */
    children?: React.ReactNode;
    className?: string;
}) {
    const { getText, date, children, className } = props;

    return (
        <div
            // Focusable controls keep the row visible while focused, so it works by keyboard.
            className={clsx(
                'flex items-center gap-0.5 opacity-0 transition-opacity group-hover/message:opacity-100 focus-within:opacity-100',
                className
            )}
        >
            {children}
            <CopyMessageButton getText={getText} />
            {date ? (
                <DateShortRelative
                    value={new Date(date)}
                    className="px-1 text-ui-small text-muted"
                />
            ) : null}
        </div>
    );
}

/**
 * Put the message's text on the clipboard, confirming with a check on the control itself.
 */
function CopyMessageButton(props: { getText: () => string }) {
    const { getText } = props;
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        if (!copied) {
            return;
        }
        const timeout = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timeout);
    }, [copied]);

    return (
        <Button
            kind="blank"
            size="xsmall"
            iconOnly
            leadingIcon={copied ? Icon.Check : Icon.Copy}
            label="Copy message"
            onPress={() => {
                void navigator.clipboard.writeText(getText());
                setCopied(true);
            }}
        />
    );
}
